/**
 * TOSS Web Application - Sidebar Component
 *
 * The internal application's side navigation — its half of the shared rail
 * (`components/sidebar/**`), which the customer portal fills with its own menu. Since 2026-08-26
 * the frame, the row, the flyout and the account block are that layer's; what is here is what only
 * a staff session has.
 *
 * What the move changed, and what the owner asked for by pointing at the cabinet: 52 px rows with
 * 24 px glyphs became 40 (44 under a finger, in the mobile drawer), and the active row stopped
 * being `bg-[#FAFAFA]` — a white one step from the background, so the lit item was found by
 * elimination rather than seen. Five hardcoded hex colours went with it.
 *
 * Its own, and deliberately so:
 *
 *   - **The ⌘K search row.** `CommandPalette` is internal tooling; the portal never mounts it.
 *   - **Permission filtering.** `isNavItemVisible` against the role and the granular key set —
 *     the reason the menu re-renders when an admin saves the matrix.
 *   - **Renamed labels.** `useT()` resolves `labelKey` at render, which is what makes the
 *     equipment-term button in the header rename a menu row.
 *   - **`onNavigate`**, which closes the mobile drawer `AppLayout` renders this in.
 */

import { forwardRef, type HTMLAttributes, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserRole } from '@/store/authStore';
import { usePermissionStore } from '@/store/permissionStore';
import { useCommandPalette } from '@/store/uiStore';
import { Search } from 'lucide-react';
import { BrandWordmark, BRAND_LABELS } from '@/components/auth';
import {
  SidebarBrand,
  SidebarNav,
  SidebarShell,
  type SidebarNavNode,
} from '@/components/sidebar';
import { useT } from '@/store/i18nStore';
import { navigationItems, isNavItemVisible, type NavItem } from './sidebar-nav/navigationConfig';
import { SidebarAccountBlock } from './sidebar-nav/SidebarAccountBlock';

// Re-exported so existing consumers (CommandPalette, AppLayout) keep importing
// the nav config from './Sidebar' unchanged.
export { navigationItems, isNavItemVisible, type NavItem } from './sidebar-nav/navigationConfig';

// ============================================================================
// Types
// ============================================================================

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /** Additional class names */
  className?: string;
  /** Whether sidebar is collapsed (icon-only mode) */
  collapsed?: boolean;
  /** Callback when toggle button is clicked */
  onToggleCollapsed?: () => void;
  /** Callback when navigation item is clicked (used to close mobile drawer) */
  onNavigate?: () => void;
  /** Как рейл стоит на странице — см. `SidebarShell`. Ящик на телефоне идёт вплотную. */
  variant?: 'card' | 'flush';
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Whether a row is the current screen.
 *
 * Prefix match, so a section stays lit while its own details are open — `/applications/42` lights
 * «Заявки», which the old strict equality did not. `/workers` is the exception it has always been:
 * «Список» must not light up on `/workers/deletion-requests`, which is its sibling, not its child.
 */
function isInternalNavItemActive(node: SidebarNavNode, pathname: string): boolean {
  if (pathname === node.path) return true;
  if (node.path === '/workers') return false;
  return pathname.startsWith(`${node.path}/`);
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Sidebar navigation component for desktop layout
 *
 * @example
 * ```tsx
 * <div className="flex">
 *   <Sidebar />
 *   <main className="flex-1">{children}</main>
 * </div>
 * ```
 */
export const Sidebar = forwardRef<HTMLElement, SidebarProps>(
  ({ className = '', collapsed = false, onToggleCollapsed, onNavigate, variant, ...props }, ref) => {
    const location = useLocation();
    const userRole = useUserRole();
    // Subscribe to the granular permission key set so the nav re-filters when
    // a user's permissions change (initial load, after admin saves the matrix).
    const keys = usePermissionStore((s) => s.keys);
    const { open: openCommandPalette } = useCommandPalette();
    const t = useT();

    const items = useMemo<SidebarNavNode[]>(() => {
      const toNode = (item: NavItem): SidebarNavNode => {
        const children = item.children
          ?.filter((child) => isNavItemVisible(child, userRole, keys))
          .map(toNode);

        return {
          id: item.id,
          label: item.labelKey ? t(item.labelKey) : item.label,
          path: item.path,
          icon: item.icon,
          children: children && children.length > 0 ? children : undefined,
        };
      };

      return navigationItems
        .filter((item) => isNavItemVisible(item, userRole, keys))
        .map(toNode);
    }, [userRole, keys, t]);

    return (
      <SidebarShell
        ref={ref}
        className={className}
        collapsed={collapsed}
        variant={variant}
        navLabel="Основное меню"
        brand={
          <SidebarBrand
            to="/"
            /* Имя продукта набирает общий лок-ап: с 27.08.2026 его печатает и экран входа,
               и разъехаться разметкой им больше негде. */
            title={<BrandWordmark surface="internal" />}
            label={BRAND_LABELS.internal}
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            onNavigate={onNavigate}
          />
        }
        top={
          !collapsed ? (
            <div className="px-3 pb-2 flex-shrink-0">
              <button
                type="button"
                onClick={openCommandPalette}
                // Заливка вместо белой карточки с тенью: на тёмном рейле тень не видна,
                // а роль «здесь можно нажать» берёт на себя светлое пятно.
                className="flex w-full items-center gap-2 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--sidebar-hover-bg)] px-3 py-2 text-left transition-colors hover:bg-[var(--sidebar-row-active-bg)]"
                aria-label="Поиск"
              >
                <span className="flex-shrink-0 text-[var(--color-text-secondary)]">
                  <Search className="w-5 h-5" />
                </span>
                <span className="flex-1 text-sm text-[var(--color-text-secondary)] truncate">
                  Поиск
                </span>
                <span className="flex-shrink-0 rounded-md border border-[var(--color-border)] px-1.5 py-px text-xs font-medium text-[var(--color-text-secondary)]">
                  ⌘K
                </span>
              </button>
            </div>
          ) : undefined
        }
        footer={<SidebarAccountBlock collapsed={collapsed} onNavigate={onNavigate} />}
        {...props}
      >
        <SidebarNav
          items={items}
          currentPath={location.pathname}
          isActive={isInternalNavItemActive}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      </SidebarShell>
    );
  }
);

Sidebar.displayName = 'Sidebar';

export default Sidebar;
